import { Upload } from 'lucide-react';
import { type ReactNode, useId, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Alert, AlertDescription } from '@/design/components/alert';
import { Input } from '@/design/components/input';
import { Label } from '@/design/components/label';
import type { ImageRefusal } from '@/features/persons/images/accept';
import { FILE_ACCEPT, refusalFor } from '@/features/persons/images/accept';

/**
 * Choosing a photograph from the machine.
 *
 * **The refusal happens while the picker is still on screen.** `accept.ts` states why that is a
 * courtesy rather than a control; what it buys is that an operator who chose the wrong file is told
 * before they have watched an upload finish.
 *
 * **The progress is INDETERMINATE, and that is honest rather than lazy.** `fetch` reports nothing
 * about how much of a request body has been sent — request-body streaming is not available in the
 * browsers this product supports — so a percentage would have to be invented. The alternatives were
 * both worse: a fake bar that jumps to 90% and waits is a lie an operator learns to distrust, and a
 * second upload path built on `XMLHttpRequest` to get a real one would bypass the client that
 * attaches the CSRF token, the idempotency key and the error contract. What is shown instead is that
 * something is in flight, and nothing more. The file's own size was
 * considered and rejected: it is a number about the FILE rather than about the upload's progress,
 * and offering it beside a spinner invites it to be read as one.
 */
export function ImageUpload({
  activeImages,
  uploading,
  onChoose,
}: Readonly<{
  activeImages: number;
  uploading: boolean;
  onChoose: (file: File) => void;
}>): ReactNode {
  const { t } = useTranslation();
  const controlId = useId();
  const [refusal, setRefusal] = useState<ImageRefusal | null>(null);
  const picker = useRef<HTMLInputElement | null>(null);

  return (
    <div className="flex flex-col gap-2" data-testid="image-upload">
      <Label htmlFor={controlId}>{t('person.images.uploadLabel')}</Label>
      <Input
        id={controlId}
        ref={picker}
        type="file"
        accept={FILE_ACCEPT}
        disabled={uploading}
        onChange={(event) => {
          const file = event.target.files?.[0];
          if (file === undefined) {
            return;
          }
          const found = refusalFor(file, activeImages);
          setRefusal(found);
          if (found === null) {
            onChoose(file);
          }
          // Cleared so choosing the same file twice fires again, which is what an operator expects
          // after a refusal they have just corrected on disk. Through the ref rather than through
          // the event's target, because writing into a handler's parameter is how a component ends
          // up depending on something the caller owns.
          if (picker.current !== null) {
            picker.current.value = '';
          }
        }}
      />

      {uploading && (
        <p
          role="progressbar"
          aria-label={t('person.images.uploading')}
          className="text-xs text-muted-foreground"
        >
          {t('person.images.uploading')}
        </p>
      )}

      {refusal !== null && (
        <Alert variant="destructive" role="alert" data-testid="upload-refusal">
          <AlertDescription>
            {refusal.kind === 'cap' && t('person.images.capReached', { limit: refusal.limit })}
            {refusal.kind === 'media_type' && t('person.images.wrongType')}
            {refusal.kind === 'empty' && t('person.images.emptyFile')}
          </AlertDescription>
        </Alert>
      )}

      <p className="flex items-center gap-1.5 text-xs text-muted-foreground">
        <Upload aria-hidden className="size-3" />
        {t('person.images.acceptedFormats')}
      </p>
    </div>
  );
}
